/*
 * Copyright (c) 2020. World Wide Generation Ltd
 */

import React from 'react';
import { Button } from 'reactstrap';
import { UpdateActions } from '../../../actions/universalTrackerValue';
import { getAnalytics } from '../../../services/analytics/AnalyticsService';
import { AnalyticsEvents } from '../../../services/analytics/AnalyticsEvents';
import UniversalTracker from '../../../model/UniversalTracker';
import SimpleTooltip from '../../simple-tooltip';
import { TOOLTIP } from '../../../constants/labels';
import { SurveyModelMinimalUtrv } from '../../../model/surveyData';
import { isNa, isNr, canExecuteAction } from '../../../utils/universalTrackerValue';
import {
  getSubmitAndVerifyButtonTooltip,
  getSubmitButtonTooltip,
  getVerifyButtonTooltip,
  canSubmitAndVerifyAction,
} from './toolbar/utils/action-buttons';
import { SubmitVerifiedButtonState } from './constants';

const { REJECT, VERIFY, UPDATE } = UpdateActions.STATUS;
const analytics = getAnalytics();

const getPayload = (utrv: Pick<SurveyModelMinimalUtrv, '_id' | 'initiativeId'>, utr: UniversalTracker) => ({
  utrvId: utrv._id,
  utrId: utr.getId(),
  utrCode: utr.getCode(),
  initiativeId: utrv.initiativeId,
  utrValueLabel: utr.getValueLabel(),
});

export const RejectButton = (props: QuestionSubmitButtonsProps) => {
  const { utrv, utr, disabled, handleSubmitQuestion } = props;
  const canBeRejected = canExecuteAction(utrv, REJECT);

  return <Button
    color='link'
    outline={true}
    className='ml-2 reject-btn'
    disabled={disabled || !canBeRejected}
    onClick={() => {
      analytics.track(AnalyticsEvents.SurveyReject, getPayload(utrv, utr));
      handleSubmitQuestion(REJECT);
    }}>
    Reject submission
  </Button>
}

export const VerifyButton = (props: QuestionSubmitButtonsProps) => {
  const { utrv, utr, disabled, valueHasChanged, handleSubmitQuestion } = props;

  const canBeVerified = canExecuteAction(utrv, VERIFY);
  const isDisabled = valueHasChanged || disabled || !canBeVerified;

  return (
    <SimpleTooltip text={getVerifyButtonTooltip(isDisabled)}>
      <Button
        color='primary'
        className='ml-2'
        disabled={isDisabled}
        onClick={() => {
          analytics.track(AnalyticsEvents.SurveyVerify, getPayload(utrv, utr));
          handleSubmitQuestion(VERIFY);
        }}
      >
        Verify
      </Button>
    </SimpleTooltip>
  );
}

export const SubmitAndVerifyButton = (props: QuestionSubmitButtonsProps) => {
  const { utrv, utr, disabled, valueHasChanged, formValid, handleSubmitUpdate } = props;

  const isRejectOrVerified = canSubmitAndVerifyAction(utrv, SubmitVerifiedButtonState.Enable)
  const isDisabled = !formValid || disabled || (isRejectOrVerified && !valueHasChanged);

  return (
    <SimpleTooltip text={getSubmitAndVerifyButtonTooltip(utrv, isDisabled)}>
      <Button
        color='primary'
        className='ml-2'
        disabled={isDisabled}
        onClick={(e) => {
          analytics.track(AnalyticsEvents.SurveyConfirmVerify, getPayload(utrv, utr));
          handleSubmitUpdate(e, true);
        }}
      >
        {utrv.verificationRequired ? 'Submit as verified' : 'Submit'}
      </Button>
    </SimpleTooltip>
  );
};

export const SubmitButton = (props: QuestionSubmitButtonsProps) => {
  const {
    utrv,
    utr,
    disabled,
    valueHasChanged,
    commentsEvidenceHasChanged,
    formValid,
    handleSubmitUpdate,
    isVerifier,
    isContributor,
  } = props;
  const isContributorAndVerifier = isContributor && isVerifier;
  const hasAnythingChanged = valueHasChanged || commentsEvidenceHasChanged;
  const canBeUpdated = canExecuteAction(utrv, UPDATE, isVerifier) || !utrv.verificationRequired;
  const isDisabled = disabled || !formValid || !(canBeUpdated && hasAnythingChanged);

  return (
    <SimpleTooltip id='submit' text={getSubmitButtonTooltip(utrv, isDisabled, isContributorAndVerifier)}>
      <Button
        color={'primary'}
        className='ml-2'
        disabled={isDisabled}
        onClick={(e) => {
          analytics.track(AnalyticsEvents.SurveyConfirm, getPayload(utrv, utr));
          handleSubmitUpdate(e, false);
        }}
      >
        Submit
      </Button>
    </SimpleTooltip>
  );
}

export const NAButton = (props: QuestionSubmitButtonsProps) => {
  const { utrv, utr, disabled, commentsEvidenceHasChanged, handleNA, isVerifier } = props;

  if (!handleNA) {
    return <></>;
  }

  const canAction = commentsEvidenceHasChanged || !isNa(utrv);
  const isVerified = utrv.status === UpdateActions.ACTION.UTR.VERIFIED;
  const isVerificationRequired = utrv.verificationRequired;
  const isVerificationLocked = isVerified && isVerificationRequired && !isVerifier;

  return (
    <SimpleTooltip text={TOOLTIP.notApplicable}>
      <Button
        className='mr-2'
        outline={true}
        disabled={disabled || !canAction || isVerificationLocked}
        onClick={() => {
          analytics.track(AnalyticsEvents.SurveyMarkAsNA, getPayload(utrv, utr));
          handleNA();
        }}
      >
        Mark as N/A
      </Button>
    </SimpleTooltip>
  );
};

export const NRButton = (props: QuestionSubmitButtonsProps) => {
  const { utrv, utr, disabled, commentsEvidenceHasChanged, handleNR, isVerifier } = props;

  if (!handleNR) {
    return <></>;
  }

  const canAction = commentsEvidenceHasChanged || !isNr(utrv);
  const isVerified = utrv.status === UpdateActions.ACTION.UTR.VERIFIED;
  const isVerificationRequired = utrv.verificationRequired;
  const isVerificationLocked = isVerified && isVerificationRequired && !isVerifier;

  return (
    <SimpleTooltip text={TOOLTIP.notReporting}>
      <Button
        className='mr-2'
        outline={true}
        disabled={disabled || !canAction || isVerificationLocked}
        onClick={() => {
          analytics.track(AnalyticsEvents.SurveyMarkAsNR, getPayload(utrv, utr));
          handleNR();
        }}
      >
        Mark as N/R
      </Button>
    </SimpleTooltip>
  );
};

export const PrivacyButton = (props: QuestionSubmitButtonsProps) => {
  const { utrv, disabled, handlePrivacy } = props;

  if (!handlePrivacy) {
    return <></>;
  }

  const isPrivate = utrv.isPrivate;

  return (
    <SimpleTooltip text={TOOLTIP.privacy}>
      <Button className='mr-2' outline={true} disabled={disabled} onClick={handlePrivacy}>
        {isPrivate ? 'Make public' : 'Make private'}
      </Button>
    </SimpleTooltip>
  );
};

export interface QuestionSubmitButtonsProps {
  utrv: Pick<SurveyModelMinimalUtrv, '_id' | 'initiativeId' | 'verificationRequired' | 'status' | 'value' | 'valueData' | 'isPrivate'>;
  utr: UniversalTracker;
  isContributor: boolean;
  isVerifier: boolean;
  isSurveyManager: boolean;
  disabled: boolean;
  valueHasChanged: boolean;
  commentsEvidenceHasChanged: boolean;
  formValid: boolean;
  handleSubmitUpdate: (e: React.MouseEvent, verify?: boolean) => void;
  handleSubmitQuestion: (action: string, notApplicableType?: string, autoVerify?: boolean) => void;
  handleNA: null | (() => void);
  handleNR: null | (() => void);
  handlePrivacy: null | (() => void);
}

const QuestionSubmitButtons = (props: QuestionSubmitButtonsProps) => {
  const { utrv, isContributor, isVerifier, isSurveyManager } = props;

  const isVerificationRequired = utrv.verificationRequired;
  const canBeVerified = canSubmitAndVerifyAction(utrv, SubmitVerifiedButtonState.Show);
  const canBeRejected = canExecuteAction(utrv, REJECT);

  const showRejectButton = isVerifier && isVerificationRequired && canBeRejected;
  const showSubmitButton = isContributor && isVerificationRequired;
  const showVerifiedButton = isVerifier && isVerificationRequired && canBeVerified;
  const showSubmitVerifyButton = (isContributor && isVerifier) || (!isVerificationRequired && isContributor);

  return (
    <div className='row'>
      <div className='col-6'>
        {isContributor ? <NAButton {...props} /> : null}
        {isContributor ? <NRButton {...props} />: null}
        {isSurveyManager ? <PrivacyButton {...props} /> : null}
      </div>
      <div className='col-6 text-right'>
        {showRejectButton ? <RejectButton {...props} /> : null}
        {showSubmitButton ? <SubmitButton {...props} /> : null}
        {showSubmitVerifyButton ? (
          <SubmitAndVerifyButton {...props} />
        ) : showVerifiedButton ? (
          <VerifyButton {...props} />
        ) : null}
      </div>
    </div>
  );
}

export default QuestionSubmitButtons;
